import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import './AppDark.css';
import Sidebar from './components/Sidebar';
import ImpersonationBanner from './components/ImpersonationBanner';
import ContractBlockingGuard from './components/ContractBlockingGuard';
import ErrorBoundary from './components/ErrorBoundary';
import { ShortcutHelpModal } from './components/ShortcutHelpModal';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { Keyboard, Loader2 } from 'lucide-react';

// Landing Page
import LandingUltimate from './pages/LandingUltimate';

// Auth & Onboarding Pages
import Register from './pages/Register';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Onboarding from './pages/Onboarding';
import AcceptInvite from './pages/AcceptInvite';

// POS App Pages - VEREINFACHT: Kassier entfernt, nutze /kasse stattdessen
// import Kassier from './pages/Kassier'; // ENTFERNT
import Dashboard from './pages/DashboardHome';
import Transactions from './pages/Transactions';
import PortalOrders from './pages/PortalOrders';
import Lieferungen from './pages/Lieferungen';
import FlottoDriver from './pages/FlottoDriver';
import Lieferportale from './pages/Lieferportale';
import Kassenabschluss from './pages/Kassenabschluss';
import Berichte from './pages/Berichte';
import Fahrerabrechnung from './pages/Fahrerabrechnung';
import Stundenabrechnung from './pages/Stundenabrechnung';
import Exporte from './pages/Exporte';
import Kategorien from './pages/Kategorien';
import Produkte from './pages/Produkte';
import Bestandswarnung from './pages/Bestandswarnung';
import Einstellungen from './pages/Einstellungen';
import Bestelluebersicht from './pages/Bestelluebersicht';
import PrintAgentDashboard from './pages/PrintAgentDashboard';

// SEO & Marketing Pages (Lazy Loaded for stability)
const KassensystemBerlin = React.lazy(() => import('./pages/KassensystemBerlin'));
const KassensystemMuenchen = React.lazy(() => import('./pages/KassensystemMuenchen'));
const KassensystemHamburg = React.lazy(() => import('./pages/KassensystemHamburg'));
const KassensystemKoeln = React.lazy(() => import('./pages/KassensystemKoeln'));
const CashXVsLightspeed = React.lazy(() => import('./pages/CashXVsLightspeed'));
const CashXVsOrderbird = React.lazy(() => import('./pages/CashXVsOrderbird'));
const CashXVsReady2Order = React.lazy(() => import('./pages/CashXVsReady2Order'));
const BlogUebersicht = React.lazy(() => import('./pages/BlogUebersicht'));
const FAQKassensystem = React.lazy(() => import('./pages/FAQKassensystem'));
import Subscription from './pages/Subscription';
import Tischverwaltung from './pages/Tischverwaltung';
import CashXKasse from './pages/CashXKasse';
import CashXAdmin from './pages/CashXAdmin';
import CashXTagging from './pages/CashXTagging';
import BonEinstellungen from './pages/BonEinstellungen';
import AIMenuImport from './pages/AIMenuImport';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import LoyaltySystem from './pages/LoyaltySystem';
import QRCodeManagement from './pages/QRCodeManagement';
import GuestOrderPage from './pages/GuestOrderPage';
import LocationsManagement from './pages/LocationsManagement';
import Downloads from './pages/Downloads';
import TaxExports from './pages/TaxExports';
import KitchenDisplay from './pages/KitchenDisplay';
import ZeroSetup from './pages/ZeroSetup';
import AIKitchenDisplay from './pages/AIKitchenDisplay';
import SmartBranding from './pages/SmartBranding';
import AIMarketing from './pages/AIMarketing';
import AIPurchasing from './pages/AIPurchasing';
import GuestManager from './pages/GuestManager';
import CampaignDashboard from './pages/CampaignDashboard';
import KIControlCenter from './pages/KIControlCenter';
import Tagesassistent from './pages/Tagesassistent';
import FlottoApp from './pages/FlottoApp';
import FahrerManagement from './pages/FahrerManagement';
import TeamManagement from './pages/TeamManagement';
import SmartOnboarding from './pages/SmartOnboarding';
import PrinterManagement from './pages/PrinterManagement';
import ProductsV2 from './pages/ProductsV2';
import FlottoIntegration from './pages/FlottoIntegration';
import DruckerStation from './pages/DruckerStation';
import AutoPrint from './pages/AutoPrint';
import CustomerOrderHistory from './pages/CustomerOrderHistory';
import MegaSettings from './pages/MegaSettings';
import MasterDataHub from './pages/MasterDataHub';
import OrderRulesSettings from './pages/OrderRulesSettings';
import Products86Manager from './pages/Products86Manager';
import CouponManager from './pages/CouponManager';
import LoyaltyProgramSettings from './pages/LoyaltyProgramSettings';
import FahrerView from './pages/FahrerView';

// Lazy load heavy admin components to prevent eager evaluation
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard'));
const HQDashboard = React.lazy(() => import('./pages/HQDashboard'));
const TenantDetailView = React.lazy(() => import('./pages/TenantDetailView'));
const OrgDashboard = React.lazy(() => import('./pages/OrgDashboard'));
const AdminContracts = React.lazy(() => import('./pages/AdminContracts'));

import ContractReview from './pages/ContractReviewSimple';

// Legal Pages & Components
import LegalPage from './pages/LegalPage';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

// Auth Protection
import ProtectedRoute from './components/ProtectedRoute';

// Admin Pages
import { 
  AdminLayout, 
  AdminDashboard, 
  AdminAccounts, 
  AdminAccountDetail, 
  AdminAuditLog,
  AdminWebhooks,
  AdminEmail,
  AdminEmailTriggers,
  AdminReports
} from './pages/admin';

// POS App Content (with Sidebar)
function POSAppContent() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Global keyboard shortcuts (now inside Router context)
  useKeyboardShortcuts({
    onOpenHelp: () => setShowHelpModal(true),
  });

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  return (
      <>
      {/* Impersonation Banner (erscheint über allem) */}
      <ImpersonationBanner />
      
      <div className="dark-container" style={{ display: 'flex' }}>
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        
        {/* Main Content Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Top Bar - Mobile Only */}
          <header className="md:hidden dark-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4)' }}>
              <button
                onClick={() => setSidebarOpen(true)}
                style={{
                  padding: 'var(--space-2)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ☰
              </button>
              <img 
                src="/ccash-x-logo.png" 
                alt="Cash-X" 
                style={{ height: '28px', width: 'auto' }}
              />
              <button
                onClick={() => setShowHelpModal(true)}
                data-testid="help-button-mobile"
                style={{
                  padding: 'var(--space-2)',
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Tastatur-Shortcuts (F1)"
              >
                <Keyboard size={20} />
              </button>
            </div>
          </header>

          {/* Main Content */}
          <main className="dark-page">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              {/* VEREINFACHT: Kassier-Route entfernt - nutze /kasse stattdessen */}
              <Route path="/kassier" element={<Navigate to="/kasse" replace />} />
              <Route path="/lieferungen" element={<Bestelluebersicht />} />
              <Route path="/dispatch" element={<Lieferungen />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/portal" element={<PortalOrders />} />
              <Route path="/flotto" element={<FlottoDriver />} />
              <Route path="/lieferportale" element={<Lieferportale />} />
              <Route path="/kassenabschluss" element={<Kassenabschluss />} />
              <Route path="/berichte" element={<Berichte />} />
              <Route path="/fahrerabrechnung" element={<Fahrerabrechnung />} />
              <Route path="/stundenabrechnung" element={<Stundenabrechnung />} />
              <Route path="/exports" element={<Exporte />} />
              <Route path="/kategorien" element={<Kategorien />} />
              <Route path="/produkte" element={<Produkte />} />
              <Route path="/ai-menu-import" element={<AIMenuImport />} />
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="/loyalty" element={<LoyaltySystem />} />
              <Route path="/qr-codes" element={<QRCodeManagement />} />
              <Route path="/locations" element={<LocationsManagement />} />
              <Route path="/tax-exports" element={<TaxExports />} />
              <Route path="/bestandswarnung" element={<Bestandswarnung />} />
              <Route path="/bestelluebersicht" element={<Bestelluebersicht />} />
              <Route path="/tische" element={<Tischverwaltung />} />
              <Route path="/einstellungen" element={<Einstellungen />} />
              <Route path="/bon-einstellungen" element={<BonEinstellungen />} />
              <Route path="/print-agent" element={<PrintAgentDashboard />} />
              <Route path="/drucker" element={<PrinterManagement />} />
              <Route path="/drucker-verwaltung" element={<PrinterManagement />} />
              <Route path="/produkte-v2" element={<ProductsV2 />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/branding" element={<SmartBranding />} />
              <Route path="/marketing" element={<AIMarketing />} />
              <Route path="/purchasing" element={<AIPurchasing />} />
              <Route path="/guests" element={<GuestManager />} />
              <Route path="/campaigns" element={<CampaignDashboard />} />
              <Route path="/ki-control" element={<KIControlCenter />} />
              <Route path="/tagesassistent" element={<Tagesassistent />} />
              <Route path="/fahrer-management" element={<FahrerManagement />} />
              <Route path="/team" element={<TeamManagement />} />
              <Route path="/flotto-integration" element={<FlottoIntegration />} />
              <Route path="/customers" element={<CustomerOrderHistory />} />
              <Route path="/auto-print" element={<AutoPrint />} />
              <Route path="/mega-settings" element={<MegaSettings />} />
              <Route path="/stammdaten" element={<MasterDataHub />} />
              <Route path="/bestellungsregeln" element={<OrderRulesSettings />} />
              <Route path="/86d" element={<Products86Manager />} />
              <Route path="/gutscheine" element={<CouponManager />} />
              <Route path="/treueprogramm" element={<LoyaltyProgramSettings />} />
            </Routes>
          </main>
        </div>
      </div>

      {/* Global Shortcut Help Modal */}
      <ShortcutHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
      </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ContractBlockingGuard>
          <Suspense fallback={
            <div className="min-h-screen bg-background flex items-center justify-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
            </div>
          }>
            <Routes>
              {/* Landing Page - Marketing/Public - ULTIMATE VERSION */}
              <Route path="/" element={<LandingUltimate />} />
          
          {/* Auth Pages */}
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/smart-onboarding" element={<SmartOnboarding />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          
          {/* Fahrer View - Separate Login für Fahrer */}
          <Route path="/fahrer" element={<FahrerView />} />
          
          {/* Public Pages - SEO & Marketing (Lazy Loaded) */}
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/faq" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <FAQKassensystem />
            </Suspense>
          } />
          <Route path="/faq-kassensystem" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <FAQKassensystem />
            </Suspense>
          } />
          <Route path="/faq-kassensystem-gastronomie" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <FAQKassensystem />
            </Suspense>
          } />
          
          {/* Lokale SEO-Seiten (Lazy Loaded) */}
          <Route path="/kassensystem-berlin" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <KassensystemBerlin />
            </Suspense>
          } />
          <Route path="/kassensystem-muenchen" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <KassensystemMuenchen />
            </Suspense>
          } />
          <Route path="/kassensystem-hamburg" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <KassensystemHamburg />
            </Suspense>
          } />
          <Route path="/kassensystem-koeln" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <KassensystemKoeln />
            </Suspense>
          } />
          
          {/* Vergleichs-Seiten (Lazy Loaded) */}
          <Route path="/vergleich/cashx-vs-lightspeed" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <CashXVsLightspeed />
            </Suspense>
          } />
          <Route path="/vergleich/cashx-vs-orderbird" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <CashXVsOrderbird />
            </Suspense>
          } />
          <Route path="/vergleich/cashx-vs-ready2order" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <CashXVsReady2Order />
            </Suspense>
          } />
          
          {/* Blog (Lazy Loaded) */}
          <Route path="/blog" element={
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#0B1220]"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>}>
              <BlogUebersicht />
            </Suspense>
          } />
          
          {/* Contract Review - Required before app access */}
          <Route path="/contract-review" element={<ContractReview />} />
          
          {/* POS App - Protected Area (with contract blocking + auth) */}
          <Route path="/app/*" element={
            <ProtectedRoute>
              <POSAppContent />
            </ProtectedRoute>
          } />
        
        {/* Admin Area - Protected */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/contracts" element={
          <ProtectedRoute>
            <AdminContracts />
          </ProtectedRoute>
        } />
        <Route path="/hq-dashboard" element={
          <ProtectedRoute>
            <HQDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/tenant/:tenantId" element={
          <ProtectedRoute>
            <TenantDetailView />
          </ProtectedRoute>
        } />
        
        {/* Org Dashboard - Multi-Store Management */}
        <Route path="/org/dashboard" element={<OrgDashboard />} />
        
        {/* Legacy Admin Routes (redirect to new admin) */}
        <Route path="/admin-legacy" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="accounts" element={<AdminAccounts />} />
          <Route path="accounts/:tenantId" element={<AdminAccountDetail />} />
          <Route path="audit-log" element={<AdminAuditLog />} />
          <Route path="webhooks" element={<AdminWebhooks />} />
          <Route path="email" element={<AdminEmail />} />
          <Route path="email/triggers" element={<AdminEmailTriggers />} />
          <Route path="reports" element={<AdminReports />} />
        </Route>
        
        {/* Cash-X Kasse - Standalone Fullscreen View */}
        <Route path="/kasse" element={<CashXKasse />} />
        <Route path="/kasse/admin" element={<CashXAdmin />} />
        <Route path="/kasse/tagging" element={<CashXTagging />} />
        
        {/* Public Guest Order Page - No Auth Required */}
        <Route path="/order/:tenantId/:tableId" element={<GuestOrderPage />} />
        
        {/* Kitchen Display System - Fullscreen */}
        <Route path="/kds" element={<KitchenDisplay />} />
        <Route path="/kds/:token" element={<KitchenDisplay />} />
        
        {/* Zero-Setup - KI-gestützte Einrichtung */}
        <Route path="/zero-setup" element={<ZeroSetup />} />
        
        {/* KI-Küchenleiter - Intelligente Priorisierung */}
        <Route path="/ai-kitchen" element={<AIKitchenDisplay />} />
        
        {/* Flotto.app - Fahrer-App */}
        <Route path="/flotto" element={<FlottoApp />} />
        
        {/* Redirect old routes to /app/* for backwards compatibility */}
        <Route path="/kassier" element={<RedirectToApp path="/kassier" />} />
        <Route path="/lieferungen" element={<RedirectToApp path="/lieferungen" />} />
        <Route path="/transactions" element={<RedirectToApp path="/transactions" />} />
        <Route path="/portal" element={<RedirectToApp path="/portal" />} />
        <Route path="/flotto" element={<RedirectToApp path="/flotto" />} />
        <Route path="/lieferportale" element={<RedirectToApp path="/lieferportale" />} />
        <Route path="/kassenabschluss" element={<RedirectToApp path="/kassenabschluss" />} />
        <Route path="/berichte" element={<RedirectToApp path="/berichte" />} />
        <Route path="/fahrerabrechnung" element={<RedirectToApp path="/fahrerabrechnung" />} />
        <Route path="/stundenabrechnung" element={<RedirectToApp path="/stundenabrechnung" />} />
        <Route path="/exports" element={<RedirectToApp path="/exports" />} />
        <Route path="/kategorien" element={<RedirectToApp path="/kategorien" />} />
        <Route path="/produkte" element={<RedirectToApp path="/produkte" />} />
        <Route path="/bestandswarnung" element={<RedirectToApp path="/bestandswarnung" />} />
        <Route path="/bestelluebersicht" element={<RedirectToApp path="/bestelluebersicht" />} />
        <Route path="/einstellungen" element={<RedirectToApp path="/einstellungen" />} />
        <Route path="/print-agent" element={<RedirectToApp path="/print-agent" />} />
        
        {/* Cloud Drucker Station - Standalone */}
        <Route path="/drucker-station" element={<DruckerStation />} />
        
        {/* Legal Pages */}
        <Route path="/legal/privacy" element={<LegalPage />} />
        <Route path="/legal/terms" element={<LegalPage />} />
        <Route path="/legal/cookies" element={<LegalPage />} />
        <Route path="/legal/dpa" element={<LegalPage />} />
        <Route path="/legal/imprint" element={<LegalPage />} />
        
        {/* Legacy redirects */}
        <Route path="/impressum" element={<LegalPage />} />
        <Route path="/datenschutz" element={<LegalPage />} />
        <Route path="/agb" element={<LegalPage />} />
      </Routes>
      
      {/* Cookie Consent Banner */}
      <CookieConsent />
      
      </Suspense>
      </ContractBlockingGuard>
    </Router>
    </ErrorBoundary>
  );
}

// Helper component for redirects
function RedirectToApp({ path }) {
  useEffect(() => {
    window.location.href = `/app${path}`;
  }, [path]);
  return null;
}

export default App;
